import { Labels } from '../../global/labels';
import { UIElement } from '../ui-element';
import { setInnerText, updateElement } from '../../global/ui-helpers';
import { TabIndex } from '../../global/keyboard';
import { UIIcon } from '../icon/ui-icon';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child, Children } from '../../decorators/child.decorator';
import type { AttributeValue, ElementConfig } from '../ui-element.type';
import type { TranslationLabels } from '../../types/labels.type';
import type { UINavListType, UINavListTypes } from './ui-navlist.type';
import type { UINavItem } from './ui-navitem';
import type { NullOr } from '../../global/utils.type';
import type { UINav } from './ui-nav';
import styles from './ui-navlist.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UINavList
 * @element ui-navlist
 * @classdesc Represents a class for <code>ui-navlist</code> element
 * @fires event:menu-update-shortcuts
 * @fires event:menu-back
 * @fires event:edit-shortcuts
 * @fires event:remove-shortcut
 * @fires event:add-shortcut
 * @property {HTMLHeadingElement} backNav Shortcut to Back button.
 * @property {NodeList<UINavItem>} menuItems Shortcut to menu items.
 * @property {boolean} edit {@attr edit} Whether the nav list is in edit mode.
 * @property {string} [labelBack] {@attr label-back} Label for Back button
 * @property {("secondary" | "auxiliary")} [type=secondary] {@attr type}
 *  {@desc secondary}
 *  {@desc auxiliary}
 * @example
 * <ui-navlist></ui-navlist>
 */
@Component({ tag: 'ui-navlist', styles })
export class UINavList extends UIElement {
    /**
     * @type {Labels}
     * @readonly
     */
    static get labels(): TranslationLabels.Labels {
        return Labels.attach('ui-navlist', {
            MY_SHORTCUTS: 'My shortcuts',
            REMOVE_SHORTCUT: 'Delete shortcut',
            ADD_SHORTCUT: 'Add shortcut',
            EDIT_SHORTCUTS: 'Edit shortcuts',
            FINISH_EDITING: 'Finish editing',
            BACK: 'Back',
        });
    }

    static readonly types: UINavListTypes = {
        SECONDARY: 'secondary',
        AUXILIARY: 'auxiliary',
    };

    @Attribute(Boolean) edit?: boolean;
    @Attribute(String) type: UINavListType = UINavList.types.SECONDARY;
    @Attribute(String) labelBack?: string;

    @Children('ui-navitem[item]') menuItems!: NodeListOf<UINavItem>;
    @Child('.ui-navlist__header.-backnav') backNav!: HTMLElement;

    /** @private */
    private dragTarget: NullOr<HTMLElement> = null;

    static get observedAttributes(): Array<string> {
        return ['label-back'];
    }

    /**
     * @returns {boolean}
     */
    hasActions(): boolean {
        return this.getAttribute('ref') === 'shortcuts';
    }

    /**
     * @param {string} pageId
     */
    setActiveShortcut(pageId: string): void {
        const activeLinks: Array<HTMLElement> = [].filter.call(
            this.menuItems,
            (node: UINavItem) => {
                if (pageId === node.getAttribute('item')) {
                    node.activate?.();
                    return true;
                } else {
                    node.deactivate?.();
                    return false;
                }
            }
        );
        const managePageAction = this.querySelector<HTMLElement>(
            '.ui-navlist__action.-manage-page'
        );
        if (managePageAction) {
            UIElement.mutateTo(
                managePageAction,
                this.createElement(
                    this.buildManagePageAction(activeLinks.length > 0)
                ),
                true
            );
        }
    }

    /**
     * Sets class -expanded to be true to activate the nav list
     * @returns {UIElement}
     */
    activate(): Element {
        this.setAttributes({ 'aria-hidden': 'false' });
        return this.updateClassList({ '-expanded': true });
    }

    /**
     * Removes class -expanded to be true to remove deactivate the nav list
     * @returns {UIElement}
     */
    deactivate(): Element {
        this.setAttributes({ 'aria-hidden': 'true' });
        return this.updateClassList({ '-expanded': false });
    }

    /**
     *
     * @param {Event} event
     * @fires event:menu-update-shortcuts
     */
    handleDrop(event: Event): void {
        this.dragTarget = null;
        const items = [].map.call(this.menuItems, (node: UINavItem) => {
            return node.getAttribute('item');
        });
        this.dispatchCustomEvent('menu-update-shortcuts', { items: items });
    }

    /**
     * @private
     * @param  {PointerEvent} event
     */
    private handleDragOver(event: PointerEvent): void {
        event.preventDefault();
        const targetPlace = (event.target as HTMLElement).closest<UINavItem>(
            'ui-navitem'
        );
        if (this.dragTarget) {
            const pos = targetPlace?.compareDocumentPosition(this.dragTarget);
            if (pos === 4) {
                targetPlace?.parentElement?.insertBefore(
                    this.dragTarget,
                    targetPlace
                );
            } else if (pos === 2) {
                const next = targetPlace?.nextSibling;
                if (next) {
                    targetPlace?.parentElement?.insertBefore(
                        this.dragTarget,
                        next
                    );
                } else {
                    targetPlace?.parentElement?.appendChild(this.dragTarget);
                }
            }
        }
    }

    /**
     * @private
     * @param  {PointerEvent} event
     */
    private handleDragStart(event: PointerEvent): void {
        const item = (event.target as HTMLElement).closest<UINavItem>(
            'ui-navitem'
        );
        if (!item) {
            return;
        }
        this.dragTarget = item;
    }

    /**
     * Toggle edit shortcuts.
     */
    toggleEditMode(): void {
        this.edit = !this.edit;
        const editAction = this.querySelector('.ui-navlist__action.-edit');
        if (editAction) {
            UIElement.mutateTo(
                editAction,
                this.createElement(this.buildEditAction(this.edit)),
                true
            );
        }
        [].forEach.call(this.menuItems, (node: UINavItem) => {
            node.edit = !!this.edit;
            const link = node.querySelector('a');
            if (this.edit) {
                node.addEventListener(
                    'dragover',
                    this.handleDragOver as EventListener
                );
                node.addEventListener('drop', this.handleDrop);
                link &&
                    link.addEventListener(
                        'dragstart',
                        this.handleDragStart as EventListener
                    );
            } else {
                node.removeEventListener(
                    'dragover',
                    this.handleDragOver as EventListener
                );
                node.removeEventListener('drop', this.handleDrop);
                link &&
                    link.removeEventListener(
                        'dragstart',
                        this.handleDragStart as EventListener
                    );
            }
        });
    }

    /**
     * @param {boolean} isEditMode
     * @fires event:edit-shortcuts
     * @returns {ElementConfig}
     */
    buildEditAction(isEditMode: boolean): ElementConfig {
        return {
            tagName: 'ui-navitem',
            classList: {
                'ui-navlist__action': true,
                '-edit': true,
            },
            children: [
                {
                    tagName: 'button',
                    attributes: {
                        'data-event': 'edit-shortcuts',
                        'type': 'button',
                    },
                    children: [
                        {
                            tagName: 'ui-caption',
                            children: [
                                {
                                    tagName: 'ui-icon',
                                    attributes: {
                                        glyph: 'edit',
                                        bgcolor: UIIcon.colors.TURQUOISE,
                                    },
                                },
                                isEditMode
                                    ? UINavList.labels.FINISH_EDITING
                                    : UINavList.labels.EDIT_SHORTCUTS,
                            ],
                        },
                    ],
                },
            ],
        };
    }

    /**
     * @param {boolean} hasShortcutForPage
     * @fires event:remove-shortcut
     * @fires event:add-shortcut
     * @returns {ElementConfig}
     */
    buildManagePageAction(hasShortcutForPage: boolean): ElementConfig {
        return {
            tagName: 'ui-navitem',
            classList: {
                'ui-navlist__action': true,
                '-manage-page': true,
            },
            children: [
                {
                    tagName: 'button',
                    attributes: {
                        'data-event': hasShortcutForPage
                            ? 'remove-shortcut'
                            : 'add-shortcut',
                        'type': 'button',
                    },
                    children: [
                        {
                            tagName: 'ui-caption',
                            children: [
                                {
                                    tagName: 'ui-icon',
                                    attributes: {
                                        glyph: hasShortcutForPage
                                            ? 'cross'
                                            : 'add',
                                        bgcolor: UIIcon.colors.TURQUOISE,
                                    },
                                },
                                hasShortcutForPage
                                    ? UINavList.labels.REMOVE_SHORTCUT
                                    : UINavList.labels.ADD_SHORTCUT,
                            ],
                        },
                    ],
                },
            ],
        };
    }

    /**
     * @returns {ElementConfig}
     */
    buildShortcutsActions(): ElementConfig {
        return {
            tagName: 'div',
            classList: {
                'ui-navlist__actions': true,
            },
            children: [
                this.buildEditAction(!!this.edit),
                this.buildManagePageAction(false),
            ],
        };
    }

    /**
     * @inheritDoc
     */
    override observeAttributes(
        name: AttributeValue,
        oldValue: AttributeValue,
        newValue: NullOr<string>
    ): void {
        if (this.state !== 'hydrated') {
            return;
        }

        switch (name) {
            case 'label-back':
                const headerCaptionElement =
                    this.backNav?.querySelector<Element>(
                        '.ui-navlist__header-caption'
                    );
                if (headerCaptionElement) {
                    setInnerText(
                        headerCaptionElement,
                        this.labelBack || (UINavList.labels.BACK as string)
                    );
                }
                break;
            /* istanbul ignore next */
            default:
                break;
        }
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        if (this.hasActions()) {
            this.insertElements([this.buildShortcutsActions()]);
        }

        this.setAttribute('role', 'list');
        const nav = this.closest<UINav>('ui-nav');
        (
            this.updateElement({
                attributes: {
                    'aria-hidden': 'true',
                },
                classList: {
                    '-secondary': this.type === UINavList.types.SECONDARY,
                    '-auxiliary': this.type === UINavList.types.AUXILIARY,
                    '-actions': this.hasActions(),
                },
            }) as UIElement
        ).insertElements([
            {
                tagName: 'button',
                attributes: {
                    type: 'button',
                    tabindex:
                        nav && nav.isMobileMode?.()
                            ? String(TabIndex.Active)
                            : String(TabIndex.Inactive),
                },
                classList: {
                    'ui-navlist__header': true,
                    '-backnav': true,
                    '-link': true,
                },
                children: [
                    {
                        tagName: 'ui-icon',
                        attributes: {
                            glyph: 'redesign-navigate-back',
                            color: UIIcon.colors.TEAL,
                            class: 'ui-navlist__header-icon',
                        },
                    },
                    {
                        tagName: 'span',
                        classList: {
                            'ui-navlist__header-caption': true,
                        },
                        children: [this.labelBack || UINavList.labels.BACK],
                    },
                ],
            },
            {
                tagName: 'ul',
                attributes: {
                    role:
                        this.type === UINavList.types.AUXILIARY
                            ? 'navigation'
                            : null,
                },
                classList: {
                    '-secondary-list': this.type === UINavList.types.SECONDARY,
                    '-auxiliary-list': this.type === UINavList.types.AUXILIARY,
                    '-plain-content': !this.querySelector('ui-navlist-menu'),
                },
                children: this.childNodes,
            },
        ]);
    }

    /**
     * @inheritDoc
     */
    override hydrate(): void {
        this.handleDragOver = this.handleDragOver.bind(this);
        this.handleDragStart = this.handleDragStart.bind(this);
        this.handleDrop = this.handleDrop.bind(this);
        updateElement(this.backNav, {
            events: {
                click: () => {
                    this.dispatchCustomEvent('menu-back', null);
                },
            },
        });
    }
}
